import { useEffect, useState } from "react";
import { FileText, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";

export default function AdminDokumen() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const [rejectModal, setRejectModal] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    const params = status !== "all" ? { status } : {};
    axios.get(`${API}/admin/dokumen`, { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [status]);

  const verify = async (id) => {
    try {
      await axios.put(`${API}/admin/dokumen/${id}/verify`, {});
      toast.success("Dokumen diverifikasi");
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const reject = async () => {
    try {
      await axios.put(`${API}/admin/dokumen/${rejectModal}/reject`, { admin_note: note });
      toast.success("Dokumen ditandai perlu diperbaiki");
      setRejectModal(null); setNote("");
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-dokumen-page" className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Verifikasi Dokumen</h1>
        <p className="text-sm text-[#64748B]">Periksa dan verifikasi dokumen yang diunggah jamaah.</p>
      </div>

      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="admin-dokumen-status-filter" className="h-10 w-full rounded-xl sm:w-52"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Semua Status</SelectItem>
          <SelectItem value="pending">Menunggu Verifikasi</SelectItem>
          <SelectItem value="verified">Terverifikasi</SelectItem>
          <SelectItem value="needs_revision">Perlu Perbaikan</SelectItem>
        </SelectContent>
      </Select>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={FileText} title="Belum ada dokumen" description="Dokumen jamaah akan muncul di sini setelah diunggah." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-dokumen-table">
            <TableHeader><TableRow><TableHead>Jamaah</TableHead><TableHead>Jenis</TableHead><TableHead>Tanggal Unggah</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`admin-dokumen-row-${d.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{d.jamaah_name}</TableCell>
                  <TableCell className="uppercase text-[#64748B]">{d.type}</TableCell>
                  <TableCell className="text-[#64748B]">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("id-ID") : "-"}</TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                  <TableCell className="text-right">
                    {d.status === "pending" && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" data-testid={`admin-dokumen-verify-${d.id}`} onClick={() => verify(d.id)} className="gap-1 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Check className="h-3 w-3" /> Verifikasi</Button>
                        <Button size="sm" variant="outline" data-testid={`admin-dokumen-reject-${d.id}`} onClick={() => setRejectModal(d.id)} className="gap-1 rounded-full border-red-200 text-red-600 hover:bg-red-50"><X className="h-3 w-3" /> Perbaikan</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!rejectModal} onOpenChange={(v) => !v && setRejectModal(null)}>
        <DialogContent data-testid="admin-dokumen-reject-modal">
          <DialogHeader><DialogTitle>Catatan Perbaikan Dokumen</DialogTitle></DialogHeader>
          <Textarea data-testid="admin-dokumen-reject-note" placeholder="Jelaskan yang perlu diperbaiki..." value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          <Button data-testid="admin-dokumen-reject-confirm" onClick={reject} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Kirim</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
